import * as Sentry from "@sentry/react";
import {
  browserApiErrorsIntegration,
  browserSessionIntegration,
  captureConsoleIntegration,
  eventFiltersIntegration,
  httpClientIntegration,
  reactRouterV6BrowserTracingIntegration,
  browserProfilingIntegration,
} from "@sentry/react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import "./i18n";

// Redux
import { store } from "redux/store";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { getEnv } from "utils/hostname";
import { APP_VERSION } from "constants/variables";
import App from "./App";

const isProduction = getEnv() === "production";

if (isProduction) {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    release: `g-web@${APP_VERSION}`, // TODO: Enviroment
    environment: "production",
    sendDefaultPii: isProduction,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    sampleRate: isProduction ? 0.1 : 0.5,
    tracePropagationTargets: [],
    integrations: [
      browserApiErrorsIntegration({
        setTimeout: true,
        setInterval: true,
        requestAnimationFrame: true,
        XMLHttpRequest: true,
        eventTarget: true,
      }),
      captureConsoleIntegration({ levels: ["error"] }),
      Sentry.breadcrumbsIntegration(),
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
      Sentry.dedupeIntegration(),
      Sentry.linkedErrorsIntegration(),
      eventFiltersIntegration({
        denyUrls: [
          /diswdgcu9cfva\.cloudfront\.net/,
          "localhost",
          /s3\.amazonaws\.com/,
          /mc\.yandex\.ru/,
          /yandex\.com/,
          /.*\.webp$/,
        ],
        ignoreTransactions: ["healthcheck", "ping"],
      }),
      ...(isProduction
        ? [
            reactRouterV6BrowserTracingIntegration({
              useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
            browserProfilingIntegration(),
            browserSessionIntegration(),
            httpClientIntegration({ failedRequestStatusCodes: [[500, 599]] }),
          ]
        : []),
    ],
    tracesSampleRate: undefined,
    tracesSampler: (samplingContext) => {
      const { name, inheritOrSampleWith } = samplingContext;
      if (name?.includes("healthcheck")) {
        return 0;
      }
      if (name?.includes("login")) {
        return 1.0;
      }
      if (name?.includes("comment")) {
        return 0.01;
      }
      return inheritOrSampleWith(0.1);
    },
    profilesSampleRate: isProduction ? 0.1 : 0,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      /Network Error/,
      "TypeError: Cannot read property",
      /Failed to load.*mc\.yandex\.ru/,
      /Image load timeout/,
    ],
    beforeSend(event) {
      const errorMessage =
        event.message || event.exception?.values?.[0]?.value || "";
      if (errorMessage.includes("Image load timeout")) {
        return null;
      }
      if (
        event.request?.url &&
        event.request.url.includes("diswdgcu9cfva.cloudfront.net")
      ) {
        return null;
      }
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      const hasThirdPartyFrame = frames.some(
        (frame) =>
          frame.filename &&
          (frame.filename.includes("node_modules") ||
            frame.filename.includes("third-party") ||
            frame.filename.includes("yandex") ||
            frame.filename.includes("amazonaws")),
      );
      if (hasThirdPartyFrame) {
        return null;
      }
      const modifiedEvent = { ...event };
      if (modifiedEvent.user) {
        delete modifiedEvent.user.email;
        delete modifiedEvent.user.ip_address;
      }
      return modifiedEvent;
    },
    beforeSendTransaction(event) {
      if (event.transaction?.includes("healthcheck")) {
        return null;
      }

      const modifiedEvent = { ...event };
      if (modifiedEvent.user) {
        delete modifiedEvent.user.email;
        delete modifiedEvent.user.ip_address;
      }
      return modifiedEvent;
    },
    beforeSendSpan(span) {
      if (span.op === "http" && span.status === "ok") {
        return span;
      }
      if (span.op === "db") {
        return span;
      }
      return {
        ...span,
        data: {},
        status: "cancelled",
        timestamp: span.start_timestamp,
      };
    },
  });
}

const root = createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <Provider store={store}>
    <App />
  </Provider>,
);
